import { buildCollection } from "@firecms/core";

import { cleanUpReplacedUploads } from "../uploadCleanup";
import {
  cards,
  ctaLabel,
  eyebrow,
  heading,
  paragraphs,
  photo,
  quote,
  section,
  subheading,
} from "./sections";

// The About Me page: one document (`pages/about`). Sections are declared in page
// order. Photos' layout, the value chips and button targets stay in the site's
// code; see docs/cms-content-model.md.

export const aboutPageCollection = buildCollection({
  id: "page-about",
  path: "pages",
  name: "About Me",
  singularName: "About Me page",
  icon: "Person",
  group: "Pages",
  description:
    "Every piece of text on the About Me page, listed in the order it appears on the page.",
  // `pages` holds one document per page; this collection shows only About's.
  forceFilter: { pageId: ["==", "about"] },
  // The page exists already and the site expects it — it can be edited, but
  // not added to or removed.
  permissions: { read: true, edit: true, create: false, delete: false },
  openEntityMode: "full_screen",
  hideIdFromForm: true,
  // Hidden from the drawer; the `pageShortcut` view in App.tsx opens it directly.
  hideFromNavigation: true,
  inlineEditing: false,
  exportable: false,
  // Replacing a photo leaves the old file in the bucket unless we remove it.
  callbacks: cleanUpReplacedUploads,

  properties: {
    hero: section(
      "1. Introduction",
      "The top of the page, beside the photo of you.",
      {
        eyebrow: eyebrow(),
        heading: heading("The greeting in large hand-lettering, e.g. Hi, I'm Rose."),
        body: paragraphs("Introduction paragraphs"),
        quote: quote(true),
        photo: photo({
          name: "Your photo",
          description: "The large portrait beside this introduction.",
          storagePath: "pages/about",
          shape: "It is shown as a tall rectangle, so an upright photo suits it best.",
        }),
      },
    ),

    teachers: section(
      "2. The Animals Who Shaped Me",
      "The three cards about Ethos, Diesel and Lucy.",
      {
        eyebrow: eyebrow(),
        heading: heading(),
        subheading: subheading(),
        items: cards({
          name: "The three animals",
          description: "One card each, with its own photo.",
          count: 3,
          sortable: true,
          previewProperties: ["name", "tag"],
          properties: {
            name: {
              name: "Animal's name",
              dataType: "string",
              validation: { max: 120 },
            },
            tag: {
              name: "Small label",
              dataType: "string",
              description: "The little pill beside the name, e.g. Passed 2020.",
              validation: { max: 120 },
            },
            body: paragraphs("Their story"),
            photo: photo({
              name: "Their photo",
              description: "Shown as a square above their story.",
              storagePath: "pages/about/teachers",
              shape: "It is cropped to a square, so use the focus settings below if the crop cuts them off.",
            }),
          },
        }),
      },
    ),

    philosophy: section(
      "3. A Different Way of Understanding Animals",
      "The centred block of writing about how you see animals.",
      {
        eyebrow: eyebrow(),
        heading: heading(),
        body: paragraphs(),
      },
    ),

    moreThanAnswers: section(
      "4. More Than Answers",
      "The row of three small cards.",
      {
        eyebrow: eyebrow(),
        heading: heading(),
        items: cards({
          name: "The three cards",
          description: "Each card has its own small title and a short paragraph.",
          count: 3,
          previewProperties: ["heading"],
          properties: {
            heading: {
              name: "Card title",
              dataType: "string",
              validation: { max: 120 },
            },
            body: {
              name: "Card text",
              dataType: "string",
              multiline: true,
              validation: { max: 2000 },
            },
          },
        }),
      },
    ),

    partnersInHealing: section(
      "5. Animals Are Our Partners in Healing",
      "The stacked points, each shown in its own white box with a star.",
      {
        eyebrow: eyebrow(),
        heading: heading(),
        body: paragraphs(
          "The points",
          "Each box becomes one point on the page, in this order. Add, reorder or remove them freely.",
        ),
      },
    ),

    synchronizedHealing: section(
      "6. Healing the Relationship, Not Just the Individual",
      "The Synchronized Healing block, ending with the button through to that page.",
      {
        eyebrow: eyebrow(),
        heading: heading(),
        body: paragraphs(),
        quote: quote(false, "Highlighted box at the end"),
        ctaLabel: ctaLabel(),
      },
    ),

    sessionsWork: section(
      "7. How Sessions Work",
      "The four cards describing what a session is like.",
      {
        eyebrow: eyebrow(),
        heading: heading(),
        items: cards({
          name: "The four cards",
          description: "Each card has a short title and a paragraph.",
          count: 4,
          previewProperties: ["label"],
          properties: {
            label: {
              name: "Card title",
              dataType: "string",
              validation: { max: 120 },
            },
            body: {
              name: "Card text",
              dataType: "string",
              multiline: true,
              validation: { max: 2000 },
            },
          },
        }),
      },
    ),

    livingSpirit: section(
      "8. Living and Spirit Animals Welcome",
      "The block about working with animals who have passed.",
      {
        eyebrow: eyebrow(),
        heading: heading(),
        body: paragraphs(),
        quote: quote(false, "Highlighted box"),
      },
    ),

    values: section(
      "9. My Values",
      "The heading above the row of value chips, and the line that closes it. The chips themselves (Love, Animals, Authenticity, Community, Fun) are part of the layout and stay in the site.",
      {
        eyebrow: eyebrow(),
        heading: heading(),
        closingBody: {
          name: "Closing line",
          dataType: "string",
          multiline: true,
          description: "The small centred paragraph below the chips.",
          validation: { max: 2000 },
        },
      },
    ),

    beyondMyWork: section(
      "10. A Little More About Me",
      "The white panel with the two personal paragraphs, side by side.",
      {
        eyebrow: eyebrow(),
        heading: heading(),
        body: paragraphs(
          "The two paragraphs",
          "These sit side by side on a wide screen, so two of roughly equal length looks best.",
        ),
      },
    ),

    hopeYouLeave: section(
      "11. What I Hope You Leave With",
      "The closing block at the bottom of the page.",
      {
        eyebrow: eyebrow(),
        heading: heading(),
        body: paragraphs(),
        quote: quote(true, "Closing quote"),
        ctaLabel: ctaLabel(),
      },
    ),

    // Identifies which page this document is; set once when seeded.
    pageId: {
      name: "Page id",
      dataType: "string",
      disabled: { hidden: true },
    },
    title: {
      name: "Page title",
      dataType: "string",
      disabled: { hidden: true },
    },
  },

  // The order of the panels in the form — matches the live page top to bottom.
  propertiesOrder: [
    "hero",
    "teachers",
    "philosophy",
    "moreThanAnswers",
    "partnersInHealing",
    "synchronizedHealing",
    "sessionsWork",
    "livingSpirit",
    "values",
    "beyondMyWork",
    "hopeYouLeave",
  ],
});
